
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import totalKPIIcon from "@/components/icons/totalKPI.icon";
import completedIcon from "@/components/icons/completed.icon";
import pendingIcon from "@/components/icons/pending.icon";
//import reviewIcon from "@/components/icons/review.icon";
import CalenderIcon from "@/components/icons/calender.icon";
import { useQuery } from '@tanstack/react-query';
import { participantApi } from '../services/participant';
import { eventApi } from '../services/events';
import { resourceApi, type Resource } from '../services/resources';
import { hackathonApi } from '../services/hackathon';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeHackathonId, setActiveHackathonId] = useState<string | null>(null);

  // Fetch all hackathons to find the active one
  const { data: hackathonsResponse } = useQuery({
    queryKey: ['hackathons'],
    queryFn: hackathonApi.getHackathons,
  });

  // Find active hackathon
  useEffect(() => {
    if (hackathonsResponse?.data) {
      const activeHackathon = hackathonsResponse.data.find(h => h.isActive === true);
      if (activeHackathon) {
        setActiveHackathonId(activeHackathon._id);
      }
    }
  }, [hackathonsResponse]);


  // Helper: convert date to relative label safely
const getRelativeDateLabel = (dateString: string) => {
  if (!dateString) return "Date unknown"

  // Try parsing the date safely
  let eventDate = new Date(dateString)
  if (isNaN(eventDate.getTime())) {
    // Try manual parsing if format is like "DD/MM/YYYY"
    const parts = dateString.split(/[\/\-]/)
    if (parts.length === 3) {
      const [day, month, year] = parts.map(Number)
      if (day && month && year) {
        eventDate = new Date(year, month - 1, day)
      }
    }
  }

  if (isNaN(eventDate.getTime())) return "Date unknown"

  const today = new Date()
  eventDate.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)

  const diffInDays = Math.round((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

  if (diffInDays === 0) return "Today"
  if (diffInDays === 1) return "Tomorrow"
  if (diffInDays > 1 && diffInDays < 7) return `In ${diffInDays} days Time`
  if (diffInDays === 7) return "In 1 week Time"
  if (diffInDays > 7 && diffInDays < 30) return `In ${Math.floor(diffInDays / 7)} week${diffInDays >= 14 ? 's' : ''} Time`
  if (diffInDays >= 30 && diffInDays < 60) return "In 1 month Time"
  if (diffInDays >= 60) return `In ${Math.floor(diffInDays / 30)} months Time`

  // Past dates
  if (diffInDays === -1) return "Yesterday"
  if (diffInDays < -1 && diffInDays > -7) return `${Math.abs(diffInDays)} days ago`
  if (diffInDays <= -7 && diffInDays > -30) return `${Math.floor(Math.abs(diffInDays) / 7)} week${Math.abs(diffInDays) >= 14 ? 's' : ''} ago`
  if (diffInDays <= -30) return `${Math.floor(Math.abs(diffInDays) / 30)} month${Math.abs(diffInDays) >= 60 ? 's' : ''} ago`

  return "Date unknown"
}

  // Fetch participants data for active hackathon
  const { data: participantsData } = useQuery({
    queryKey: ['participants-count', activeHackathonId],
    queryFn: () => participantApi.getParticipantsByHackathon(activeHackathonId!, { page: 1, limit: 1 }),
    enabled: !!activeHackathonId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch teams data for active hackathon
  const { data: teamsData } = useQuery({
    queryKey: ['teams-count', activeHackathonId],
    queryFn: () => participantApi.getTeamsByHackathon(activeHackathonId!, { page: 1, limit: 1 }),
    enabled: !!activeHackathonId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch events data for active hackathon
  const { data: eventsData } = useQuery({
    queryKey: ['events-dashboard', activeHackathonId],
    queryFn: () => eventApi.getEvents(activeHackathonId!),
    enabled: !!activeHackathonId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch resources data for active hackathon
  const { data: resourcesData } = useQuery({
    queryKey: ['resources-dashboard', activeHackathonId],
    queryFn: () => resourceApi.getResources({ page: 1, limit: 3 }),
    enabled: !!activeHackathonId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract counts from API data
  const totalParticipants = participantsData?.pagination?.totalItems || 0;
  const totalTeams = teamsData?.pagination?.totalItems || 0;
  const submissions = 0; // This would need a separate API endpoint





  const parseEventDate = (dateString: string) => {
  if (!dateString) return null;

  let date = new Date(dateString);

  // If the date is invalid, try manual parsing (e.g. "DD/MM/YYYY" or "YYYY-MM-DD")
  if (isNaN(date.getTime())) {
    const parts = dateString.split(/[\/\-]/).map(Number); // splits on "/" or "-"
    if (parts.length === 3) {
      const [day, month, year] = parts.length === 3 ? parts : [0, 0, 0];
      date = new Date(year, month - 1, day);
    }
  }

  return isNaN(date.getTime()) ? null : date;
};

const upcomingEvents = (eventsData?.data || []).filter((event) => {
  const eventDate = parseEventDate(event.date);
  if (!eventDate) return false;

  const today = new Date();
  eventDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return eventDate >= today; // ✅ shows today + future, hides past
}).slice(0, 3);



  // Get resources (limit to 3)
  const resources = resourcesData?.data?.slice(0, 3) || [];

  // Stats cards data - Different for employees vs managers and views
  const statsCards = [
    {
      label: "All Participants",
      value: totalParticipants,
      icon: totalKPIIcon,
    },
    {
      label: "Total Teams",
      value: totalTeams,
      icon: completedIcon,
    },
    {
      label: "Submissions",
      value: submissions,
      icon: pendingIcon,
    },
  ];

  return (
    <div>
      <div
        className={`grid grid-cols-1 gap-6 my-8 ${
          statsCards.length === 3 ? "md:grid-cols-3" : "md:grid-cols-4"
        }`}
      >
        {statsCards.map((stat, index) => (
          <Card
            key={index}
            className="bg-white border border-gray-200 h-[113px]"
          >
            <CardContent className="px-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-950 mb-1">
                    {stat.label}
                  </p>
                  <p className="text-3xl font-bold text-gray-800">
                    {stat.value}
                  </p>
                </div>

                <stat.icon />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Events Section */}
      {upcomingEvents.length > 0 && (
        <div className="mb-8">
          <div className="mb-6 flex items-center justify-between ">
            <h2 className="text-lg font-semibold text-gray-900 ">
              Upcoming Events
            </h2>

            <button 
              onClick={() => navigate('/events')} 
              className="text-sm font-normal text-[#101828] hover:text-blue-600"
            >
              See all
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {upcomingEvents.map((event) => (
              <Card
                key={event._id}
                className="bg-white border !h-fit border-gray-200 hover:shadow-md transition-shadow !py-0 !gap-0"
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Event Image */}
                    <div className="flex-shrink-0">
                      <div className="w-fit h-[70px] rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        {event.image ? (
                          <img
                            src={event.image}
                            alt={event.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <CalenderIcon />
                        )}
                      </div>
                    </div>

                    {/* Event Details */}
                    <div className="flex-1 min-w-0 flex flex-col gap-2">
                      <h3 className="text-sm font-medium text-[#101828] ">
                        {event.title}
                      </h3>
                      <p className="text-xs text-gray-600 leading-4">
                        {event.description}
                      </p>

                      {/* Date and Time */}
                      <div className="flex items-center gap-4 text-sm text-gray-700">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-[16px] h-[16px] text-gray-500" />
                          <span className="text-xs font-medium text-[#101828]">
                             {getRelativeDateLabel(event.date)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-[16px] h-[16px] text-gray-500" />
                          <span className="text-xs font-medium text-[#101828]">
                            {event.time}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Resources Section */}
      {resources.length > 0 && (
        <div className="mb-8">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Resources
            </h2>
            <button 
              onClick={() => navigate('/resources')} 
              className="text-sm font-normal text-[#101828] hover:text-blue-600"
            >
              See all
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {resources.map((resource: Resource) => (
              <Card
                key={resource._id}
                className="bg-white border !h-fit border-gray-200 hover:shadow-md transition-shadow !py-0 !gap-0"
              >
                <CardContent className="p-4">
                  <div className="flex flex-col gap-4">
                    {/* Tag */}
                    <div className="flex items-start justify-between">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        {resource.categoryId?.name || 'General'}
                      </span>
                    </div>
                    
                    {/* Icon and Title */}
                    <div className="flex items-start gap-3">
                     
                      <h3 className="text-base font-semibold text-gray-900">
                        {resource.title}
                      </h3>
                    </div>
                    
                    {/* Description */}
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {resource.description}
                    </p>
                    
                    {/* Read More Link */}
                    <a 
                      href={resource.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-sm font-medium hover:text-blue-700 flex items-center gap-1 w-fit"
                    >
                      Read More
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
